import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { GetSiteStatusLabel, SiteStatusEnum } from '@app/enums/site-status.enum';
import { GetAllSitesResponse } from '@app/generated-api';
import { BaseSitesListComponent } from '@app/v2/components/base-sites-list/base-sites-list.component';
import { SiteService } from '@app/v2/services/site/site.service';
import { LocalStorageAgGridHandler } from '@app/v2/utils/ag-grid/local-storage-ag-grid.utils';
import { MdsDropdownComponent, MdsOption, MdsTextFieldComponent } from '@medpacesoftwaredevelopment/designsystem';
import {
    ColDef,
    GetQuickFilterTextParams,
    MdsAgGridComponent,
    RowClickedEvent,
} from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';

type AllSitesRow = GetAllSitesResponse;

type AllSitesPrefilter = {
    statusIds: number[];
};

const gridStateStorageKey = 'all-sites-table-state';

@Component({
    selector: 'medpace-sites',
    imports: [MdsAgGridComponent, MdsDropdownComponent, MdsTextFieldComponent],
    templateUrl: './sites.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SitesComponent extends BaseSitesListComponent<AllSitesRow, AllSitesPrefilter> {
    private readonly siteService = inject(SiteService);
    private readonly router = inject(Router);

    protected readonly gridStateStorageKey = gridStateStorageKey;

    public readonly statusOptions: MdsOption<number>[] = Object.values(SiteStatusEnum)
        .filter((value): value is SiteStatusEnum => typeof value === 'number')
        .map((statusId) => ({ value: statusId, viewValue: GetSiteStatusLabel(statusId) }));

    public readonly statusControl = new FormControl<number[]>(
        LocalStorageAgGridHandler.getFromLocalStorage<AllSitesPrefilter>(gridStateStorageKey)?.prefilter?.statusIds ?? [
            SiteStatusEnum.Active,
        ],
        { nonNullable: true }
    );

    private readonly selectedStatusIds: Signal<number[]> = toSignal(this.statusControl.valueChanges, {
        initialValue: this.statusControl.value,
    });

    protected override readonly prefilter: Signal<AllSitesPrefilter> = computed(() => ({
        statusIds: this.selectedStatusIds(),
    }));

    private readonly sitesResource = rxResource({
        params: () => ({ statusIds: this.selectedStatusIds() }),
        stream: ({ params }) => this.siteService.getAllSites(params.statusIds),
    });

    protected readonly rows: Signal<AllSitesRow[] | undefined> = this.sitesResource.value;

    protected readonly rowsLoading: Signal<boolean> = computed(
        () => this.sitesResource.isLoading() || !this.sitesResource.hasValue()
    );

    public readonly colDefs: ColDef<AllSitesRow>[] = [
        {
            field: 'protocol',
            headerName: 'Project ID',
        },
        {
            field: 'siteNumber',
            headerName: 'Site #',
        },
        {
            field: 'primaryCRCFullName',
            headerName: 'Active Primary CRC',
        },
        {
            field: 'institutionName',
            headerName: 'Institution Name',
        },
        {
            field: 'piLastName',
            headerName: 'PI Last Name',
        },
        {
            field: 'statusName',
            headerName: 'Portal Activation Status',
        },
        {
            field: 'clinTrakStatus',
            headerName: 'ClinTrak Status',
            hide: true,
        },
        {
            field: 'sponsor',
            headerName: 'Sponsor',
            hide: true,
        },
        {
            field: 'supportingCRCsFullNames',
            hide: true,
            lockVisible: true,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            getQuickFilterText: (params: GetQuickFilterTextParams<AllSitesRow, string[], any>) =>
                params.value?.join(',') ?? '',
        },
    ];

    public rowClick(row: RowClickedEvent<AllSitesRow>): void {
        const site = row.data;
        if (!site) {
            return;
        }

        this.router.navigate([`studies/${site.studyId}/sites/${site.id}`]);
    }
}
